<template>
  <MysteryButton
    v-if="moduleData.options.button_style === 'mystery-science' && draftIsMysteryTheme"
    class="button-module button-module--mystery"
    :class="{
      'button-module--edit-mode': mode === 'EDIT',
    }"
    :color="moduleData.options.theme && moduleData.options.theme.variation === 2
      ? 'purple'
      : 'black'"
    :link="moduleData.options.action === 'link' ? target : undefined"
    :target="moduleData.options.action === 'link' ? '_blank' : undefined"
    type="box"
    @click="navigate"
  >
    {{ decodedText }}
  </MysteryButton>
  <button
    v-else-if="moduleData.options.action === 'page'
      || moduleData.options.action === 'window'"
    class="button-module comet-button"
    :class="[
      {
        'button-module--edit-mode': mode === 'EDIT',
        'component-module-wrapper__module-contents--shape-mask': moduleData
          && moduleData.options && moduleData.options.shape
          && moduleData.options.shape !== 'default',
      },
      themeClass || compatibilityThemeClass,
    ]"
    :style="style"
    @click="navigate"
  >
    {{ decodedText }}
  </button>
  <a
    v-else-if="moduleData.options.action === 'link'"
    class="button-module comet-button"
    :class="[
      {
        'button-module--edit-mode': mode === 'EDIT',
        'component-module-wrapper__module-contents--shape-mask': moduleData
          && moduleData.options && moduleData.options.shape
          && moduleData.options.shape !== 'default',
      },
      themeClass || compatibilityThemeClass,
    ]"
    :style="style"
    :href="target"
    target="_blank"
    @click="navigate"
  >
    {{ decodedText }}
  </a>
</template>

<script>
import { MysteryButton } from '@discoveryedu/mystery-components';
import { mapActions, mapState } from 'pinia';
import { get } from 'lodash-es';
import { updateDomainForDELinks } from '@discoveryedu/titan-text-editor';
import * as types from '@/lib/constants/store';
import { decodeHTML } from '@/lib/utils';
import { formatUrl } from '@/lib/utils/url';
import navigation from '@/mixins/navigation';
import { themes } from '@/lib/constants/themes.json';
import {
  useEditorStore,
} from '@/stores';

export default {
  name: 'ButtonModule',
  components: {
    MysteryButton,
  },
  props: {
    canvasName: {
      type: String,
      required: true,
    },
    id: {
      type: String,
      required: true,
    },
    mode: {
      type: String,
      required: true,
    },
    moduleData: {
      type: Object,
      required: true,
    },
    page: {
      type: Object,
      default: null,
    },
  },
  mixins: [navigation],
  computed: {
    ...mapState(useEditorStore, [
      'currentPage',
      'domains',
    ]),
    compatibilityThemeClass() {
      const backgroundColor = get(this, 'moduleData.options.style.background_color');
      return backgroundColor && `editor-theme__button--${backgroundColor.replace('#', '')}`;
    },
    decodedText() {
      // Descriptions are escaped on the BE and must be unescaped (e.g. &amp; => &)
      if (!this.moduleData.options) return '';
      return decodeHTML(this.moduleData.options.text);
    },
    draftIsMysteryTheme() {
      return get(this, 'currentPage.options.theme.name') === 'mystery-science';
    },
    style() {
      // New variation-based themes override the old style-based themes
      if (this.themeClass) return null;

      // Note: This purposely leaves out the background color, which is handled in css
      // so it can receive a hover state
      return this.moduleData.options.style;
    },
    target() {
      const target = formatUrl(get(this, 'moduleData.options.target', ''));

      if (this.mode === 'VIEW') {
        return updateDomainForDELinks(target, this.domains.appDomain);
      }

      return target;
    },
    theme() {
      const themeId = (this.page || this.currentPage)?.options?.theme?.name;
      return themes.find((theme) => theme.id === themeId);
    },
    themeClass() {
      let variation = get(this, 'moduleData.options.theme.variation');
      if (!variation) return undefined;
      if (this.theme) {
        const hasButtonColor = !!(this.theme.buttonColors || []).find((c) => c.id === variation);
        if (!hasButtonColor) variation = 1;
      }
      return `editor-theme__button--${variation}`;
    },
  },
  methods: {
    ...mapActions(useEditorStore, [
      types.VIEW_MODAL_CANVAS_BY_ID,
      types.VIEW_PREV_PAGE,
      types.VIEW_NEXT_PAGE,
      types.VIEW_PAGE_BY_ID,
    ]),
    navigate(e) {
      this.handleViewModeAnchorClick(e);
      if (this.mode === 'EDIT') {
        // In edit mode, we should never navigate on click
        e.preventDefault();
      } else if (this.moduleData.options.action === 'page') {
        // Otherwise navigate if it's a button (anchor links handle themselves)
        const targetPage = this.moduleData.options.target_page;

        if (targetPage === 'PREV') {
          this[types.VIEW_PREV_PAGE]();
        } else if (targetPage === 'NEXT') {
          this[types.VIEW_NEXT_PAGE]();
        } else {
          this[types.VIEW_PAGE_BY_ID](targetPage);
        }
      } else if (this.moduleData.options.action === 'window') {
        this[types.VIEW_MODAL_CANVAS_BY_ID](this.moduleData.options.target_page);
      }
    },
  },
};
</script>

<style lang="stylus">
.button-module {
  align-items: center;
  break-inside: avoid; // Avoid splitting across pages when printing
  display: flex;
  height: 100%;
  justify-content: center;
  margin: 0;
  padding: 0;
  width: 100%;

  &--mystery {
    // Leave room for the button shadow within the bounds of the module
    height: calc(100% - 6px);
    width: calc(100% - 6px);
  }

  &--edit-mode {
    // Button area is draggable so show move cursor instead
    cursor: move;
  }

  html[dir="rtl"] & {
    margin-right: 0;
  }
}
</style>
